import React, { useContext, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";

import "@css/main.css";
import "@css/dark.css";
import "@css/layout.css";

import { LanguageContext } from "@lib/i18n/LanguageProvider";
import LanguageSelector from "@lib/i18n/LanguageSelector";
import ThemeToggle from "@layout/ThemeToggle";
import FullScreenToggle from "@layout/FullScreenToggle";
import { FaInfoCircle, FaVideo } from "react-icons/fa";
import Sidebar from "./Sidebar";
import LoginDropdown from "@layout/LoginDropdown";
import packageJson from "../../../../package.json";
import { GlobalOperations } from "config/GlobalApiOperations";

const Layout: React.FC = ({ }) => {
  const context = useContext(LanguageContext);
  const { translations = {}, lang = "tr-TR" } = context || {};

  const location = useLocation();
  const pathname = location.pathname;
  const activePage = pathname.split("/").slice(-1)[0];
  const version = packageJson.version;
  const isProd = GlobalOperations.isProd;

  const [layoutContext, setLayoutContext] = useState<string>("");
  const [layoutHeaderImage, setLayoutHeaderImage] = useState<string>(
    require("../../../assets/images/header/top-header.png")
  );
  const [pageTitle, setPageTitle] = useState<string>("Page Title");
  const [pageSummary, setPageSummary] = useState<string>("Page Summary");
  const [videoHelpUrl, setVideoHelpUrl] = useState<string>("");
  const [showHeader, setShowHeader] = useState<boolean>(false);
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const [showSummaryOnMobile, setShowSummaryOnMobile] =
    useState<boolean>(false);

  const [logo, setLogo] = useState<string | null>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <>
      {showSidebar && (
        <aside
          className={`sidebar sidebar-base ${isMenuOpen ? "" : "sidebar-white left-bordered sidebar-mini"
            }`}
          data-toggle="main-sidebar"
          data-sidebar="responsive"
        >
          <div
            className="sidebar-header d-flex align-items-center justify-content-start"
            style={{ height: "70px" }}
          >
            <div
              className="navbar-brand"
              style={{
                maxWidth: isMenuOpen ? "280px" : "200px",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              <div className="logo-main">
                {logo ? (
                  <>
                    <Link to={`/${lang}/Index`}>
                      <div className="logo-normal">
                        <img src={logo} className="w-80 h-80" alt="Logo" />
                      </div>
                      <div className="logo-mini">
                        <img src={logo} className="w-80 h-80" alt="Logo" />
                      </div>
                    </Link>
                  </>
                ) : (
                  <div className="placeholder-logo d-flex align-items-center">
                    <svg
                      className="icon-30 text-primary"
                      xmlns="http://www.w3.org/2000/svg"
                      width="30"
                      height="30"
                      viewBox="0 0 108 108"
                    >
                      <g
                        transform="translate(0,108) scale(0.1,-0.1)"
                        fill="currentColor"
                        stroke="none"
                      >
                        <path d="M397 1045 c-138 -38 -272 -146 -332 -269 -69 -139 -80 -287 -31 -421 l21 -60 290 4 c426 5 429 7 124 64 -140 27 -258 52 -262 56 -17 17 -26 136 -16 211 24 171 124 315 275 398 l59 31 -40 0 c-22 -1 -62 -7 -88 -14z" />
                        <path d="M547 1038 c-8 -13 -72 -131 -142 -263 -70 -132 -139 -260 -152 -285 -30 -54 -47 -72 180 193 117 137 167 187 182 187 44 0 167 -72 229 -134 105 -105 156 -229 156 -381 l1 -80 19 35 c33 61 53 174 47 260 -17 223 -169 410 -386 474 -78 23 -117 21 -134 -6z" />
                        <path d="M624 805 c4 -11 46 -128 92 -259 l84 -240 -31 -32 c-61 -63 -191 -118 -306 -129 -111 -11 -247 26 -352 96 l-34 23 13 -25 c30 -54 135 -146 210 -181 167 -80 329 -77 491 8 65 34 189 149 189 175 0 10 -336 557 -353 574 -7 7 -7 3 -3 -10z" />
                      </g>
                    </svg>
                    <h6
                      className="logo-title ms-3"
                      style={{
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        maxWidth: "275px",
                      }}
                    >
                      base-react-ts
                    </h6>
                  </div>
                )}
              </div>
            </div>
            <div
              className="sidebar-toggle"
              data-toggle="sidebar"
              data-active="true"
              onClick={toggleMenu}
            >
              <i className="icon">
                <svg
                  className="icon-20"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.25 12.2744L19.25 12.2744"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                  <path
                    d="M10.2998 18.2988L4.2498 12.2748L10.2998 6.24976"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </i>
            </div>
          </div>
          <div className="sidebar-body pt-0 data-scrollbar">
            <Sidebar activePage={activePage} toggleMenu={toggleMenu} />
          </div>
          <div className="sidebar-footer"></div>
        </aside>
      )}
      <main className="main-content">
        <div className="position-relative iq-banner">
          <nav
            className="nav navbar navbar-expand-xl navbar-light iq-navbar header-hover-menu left-border"
            style={{ height: "70px" }}
          >
            <div className="container-fluid navbar-inner">
              <div className="d-flex align-items-center justify-content-between product-offcanvas">
                {showSidebar && (
                  <div
                    className="sidebar-toggle"
                    data-toggle="sidebar"
                    data-active="true"
                    onClick={toggleMenu}
                  >
                    <i className="icon">
                      <svg className="icon-20" width="20" viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M4,11V13H16L10.5,18.5L11.92,19.92L19.84,12L11.92,4.08L10.5,5.5L16,11H4Z"
                        />
                      </svg>
                    </i>
                  </div>
                )}
              </div>
              <div className="align-items-center col-auto d-flex">
                {/* d-sm-flex d-none */}
                <div className="d-sm-flex d-none">{layoutContext}</div>

                {!isProd && (
                  <div className="d-sm-flex d-none ms-1 me-1">
                    <span className="badge rounded-pill bg-secondary">
                      v{version}
                    </span>
                  </div>
                )}


                <li className="nav-item dropdown"></li>

                {/* {lang && (
                  <div className="m-2">
                    <LanguageSelector />
                  </div>
                )} */}

                <div className="d-flex">
                  <ThemeToggle />
                </div>

                <div className="d-sm-flex d-none">
                  <FullScreenToggle />
                </div>
                {/* <div className="d-sm-flex d-none">
                  <LoginDropdown />
                </div> */}
              </div>
            </div>
          </nav>
          {showHeader ? (
            <div className="iq-navbar-header" style={{ height: "200px" }}>
              <div>
                <div className="container-fluid iq-container">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="text-content">
                          {pageTitle && <h1>{pageTitle}</h1>}
                          {pageSummary && (
                            <p
                              className={
                                showSummaryOnMobile ? "" : "d-none d-md-block"
                              }
                            >
                              {pageSummary}
                            </p>
                          )}
                        </div>
                        {videoHelpUrl && (
                          <div className="video-content">
                            <a
                              href={videoHelpUrl}
                              target="_blank"
                              className="btn btn-link btn-soft-light"
                              lang-tr="Yardım"
                            >
                              {/* <FaVideo /> {translations["help"]} */}
                              <FaVideo /> {translations["help"]}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="iq-header-img" style={{ top: "-20px" }}>
                <img
                  src={layoutHeaderImage}
                  alt="header"
                  className="theme-color-default-img img-fluid w-100 h-100 animated-scaleX"
                  loading="lazy"
                />
              </div>
            </div>
          ) : (
            <div className="iq-navbar-header" style={{ height: "100px" }}></div>
          )}
        </div>
        <Outlet
          context={{
            setLayoutContext,
            setLayoutHeaderImage,
            setPageTitle,
            setPageSummary,
            setVideoHelpUrl,
            setShowHeader,
            setShowSidebar,
            setShowSummaryOnMobile,
          }}
        />
        {!isProd && (
          <div
            className="btn-download btn btn-warning py-2 px-3 d-flex gap-2"
            style={{ cursor: "none", pointerEvents: "none" }}
          >
            <FaInfoCircle size={20} />
            Test Ortamı (v{version})
          </div>
        )}
      </main>
    </>
  );
};

export default Layout;
